import { ArrowRight, Camera, MessageSquareText, MapPin, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

const links = [
  {
    eyebrow: "SEPT 25 · NEW ORLEANS",
    title: "See the fotos",
    copy: "Find and save your Pole & Polaroids event images in the Fotos on the Geaux gallery.",
    href: "https://fotos.geauxfotos.com/e/pole-polaroids",
    external: true,
    Icon: Camera,
  },
  {
    eyebrow: "TELL US EVERYTHING",
    title: "Leave feedback",
    copy: "Rate the experience, tell us what should change, and help shape the next edition.",
    href: "https://fotos.geauxfotos.com/e/pole-polaroids/feedback",
    external: true,
    Icon: MessageSquareText,
  },
  {
    eyebrow: "2027",
    title: "Follow the tour",
    copy: "Join a city list, see target markets, or inquire about bringing Pole & Polaroids to your city.",
    href: "/tour",
    external: false,
    Icon: MapPin,
  },
];

export default function PoleHub() {
  return (
    <main className="min-h-screen bg-[#070707] text-[#F5F0E8] selection:bg-[#F5F0E8] selection:text-black">
      <Seo
        title="Pole & Polaroids | Fotos on the Geaux"
        description="Pole & Polaroids is the New Orleans-born movement and editorial photography experience by Fotos on the Geaux. View event fotos, leave feedback and follow the tour."
        path="/pole-and-polaroids"
        imageAlt="Pole & Polaroids by Fotos on the Geaux"
      />

      <div className="fixed inset-0 pointer-events-none opacity-70" style={{
        background:
          "radial-gradient(60% 55% at 12% 18%, rgba(227,38,54,.20), transparent), radial-gradient(55% 55% at 88% 14%, rgba(23,92,255,.18), transparent), radial-gradient(50% 45% at 55% 92%, rgba(138,53,242,.16), transparent)"
      }} />

      <nav className="sticky top-0 z-40 border-b border-white/10 bg-[#070707]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl min-w-0 items-center justify-between gap-2 px-4 py-3 sm:gap-3 sm:px-5 sm:py-4">
          <span className="shrink-0 font-accent text-xs tracking-[.18em] sm:text-sm sm:tracking-[.22em]">P&amp;P<span className="text-[#E32636]">.</span></span>
          <a
            href="https://fotos.geauxfotos.com/e/pole-polaroids/feedback"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Leave feedback about the Pole &amp; Polaroids experience"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E32636]/60 bg-[#E32636]/10 min-h-11 px-3 py-2 font-accent text-[10px] tracking-[.08em] text-[#F5F0E8] transition hover:border-[#E32636] hover:bg-[#E32636]/20 sm:gap-2 sm:px-4 sm:text-[10px] sm:tracking-[.14em]"
          >
            LEAVE FEEDBACK <ArrowRight size={13} />
          </a>
          <a
            href="https://geauxfotos.com/"
            className="shrink-0 rounded-full border border-white/15 min-h-11 px-3 py-2 font-accent text-[10px] tracking-[.08em] text-white/80 transition hover:border-white/35 hover:text-white sm:px-4 sm:text-[9px] sm:tracking-[.18em]"
          >
            FOTOS HOME
          </a>
        </div>
      </nav>

      <section className="relative z-10 mx-auto flex min-h-[64svh] max-w-5xl min-w-0 flex-col justify-end overflow-hidden px-4 pb-14 pt-16 sm:min-h-[70svh] sm:px-5 sm:pt-20 md:pb-20">
        <p className="max-w-full font-accent text-[9px] tracking-[.24em] text-[#39E75F] sm:text-[10px] sm:tracking-[.38em]">A FOTOS ON THE GEAUX EXPERIENCE</p>
        <h1 className="mt-5 w-full max-w-full font-display font-black leading-[.78] tracking-[-.065em]">
          <span className="block whitespace-nowrap text-[clamp(4.35rem,20vw,10rem)]">POLE</span>
          <span className="block whitespace-nowrap italic text-[clamp(2.1rem,8.9vw,7.25rem)] md:text-[8.75rem]">&amp; POLAROIDS</span>
        </h1>
        <p className="mt-8 max-w-2xl text-[clamp(.95rem,4vw,1.125rem)] leading-7 text-white/62">
          Movement. confidence. editorial flash photography. physical fotos. A New Orleans-born experience built to keep growing after the night ends.
        </p>
      </section>

      <section className="relative z-10 border-y border-white/10 bg-black/25">
        <div className="mx-auto grid max-w-5xl gap-px bg-white/10 md:grid-cols-3">
          {links.map(({ eyebrow, title, copy, href, external, Icon }) => {
            const content = (
              <>
                <div className="flex items-center justify-between">
                  <Icon size={19} className="text-[#39E75F]" />
                  <ArrowRight size={17} className="text-white/35 transition group-hover:translate-x-1 group-hover:text-white" />
                </div>
                <p className="mt-10 font-accent text-[9px] tracking-[.25em] text-white/38">{eyebrow}</p>
                <h2 className="mt-2 max-w-full font-display text-[clamp(2.15rem,10vw,2.75rem)] font-bold leading-[.95]">{title}</h2>
                <p className="mt-4 text-base leading-6 text-white/65">{copy}</p>
              </>
            );
            const cls = "group min-h-[240px] min-w-0 bg-[#0a0a0a] p-5 transition hover:bg-[#101010] sm:min-h-[280px] sm:p-7";
            return external ? (
              <a key={title} href={href} target="_blank" rel="noopener noreferrer" className={cls}>{content}</a>
            ) : (
              <Link key={title} to={href} className={cls}>{content}</Link>
            );
          })}
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-5xl overflow-hidden px-5 py-20">
        <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden" aria-hidden="true">
          {[
            ["8%","18%","0s"],["18%","62%","1.2s"],["32%","28%",".6s"],["48%","12%","1.8s"],
            ["62%","72%",".9s"],["74%","24%","2.1s"],["88%","58%",".3s"],["94%","16%","1.5s"]
          ].map(([left, top, delay], i) => (
            <span
              key={i}
              className="absolute text-[#f4d79b] opacity-70 animate-pulse drop-shadow-[0_0_8px_rgba(244,215,155,.85)]"
              style={{ left, top, animationDelay: delay, fontSize: i % 3 === 0 ? "18px" : "12px" }}
            >✦</span>
          ))}
        </div>
        <div className="relative z-10 overflow-hidden border border-white/10 bg-[#0b0b0b] shadow-[0_0_60px_rgba(227,38,54,.08)]">
          <div className="grid md:grid-cols-[1.05fr_.95fr]">
            <img
              src="/assets/naughty-list/mrs-claus-sparkles.png"
              alt="Sparkling Mrs. Claus holiday Pole & Polaroids campaign portrait"
              loading="lazy" decoding="async" className="float-img h-full min-h-[320px] w-full object-cover object-center"
              style={{ "--float-distance": "-8px", "--float-duration": "7s" }}
            />
            <div className="flex flex-col justify-center p-8 md:p-10">
              <Sparkles size={22} className="text-[#E32636]" />
              <p className="mt-7 font-accent text-[9px] tracking-[.3em] text-white/40">WHAT'S NEXT</p>
              <h2 className="mt-3 max-w-full font-display text-[clamp(2.6rem,11vw,3rem)] font-black leading-[.92]">The Naughty List.</h2>
              <p className="mt-6 text-base leading-7 text-white/65">
                December 18, 2026. Pole & Polaroids returns with The Naughty List. Take a first look at the upcoming holiday edition.
              </p>
              <Link to="/pole-and-polaroids/naughty-list" className="mt-7 inline-flex w-fit items-center gap-2 rounded-full bg-[#F5F0E8] px-6 py-3 font-accent text-[9px] tracking-[.18em] text-black">
                PREVIEW THE EVENT <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/10 px-5 py-8">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 text-xs text-white/35 sm:flex-row sm:items-center sm:justify-between">
          <strong className="font-accent tracking-[.2em] text-white/55">POLE &amp; POLAROIDS</strong>
          <span>by Fotos on the Geaux · New Orleans</span>
        </div>
      </footer>
    </main>
  );
}
